/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleCustomerShow } from 'src/components/TradeRule';
import TradeRuleCustomerShowPlaceholder from 'src/components/TradeRule/TradeRuleCustomerShow/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule_customer_show_route.initial_load_error.message',
    defaultMessage: 'An error occurred while loading trade rules. Please try again.',
  },
});

export const tradeRuleCustomerShowRouteQuery = graphql`
  query TradeRuleCustomerShowRoute_Query($uuid: ID!) {
    viewer {
      ...TradeRuleCustomerShow_viewer @arguments(uuid: $uuid)
    }
  }
`;

const tradeRuleCustomerShowRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeRuleCustomerShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule customer show route content.
 */
function TradeRuleCustomerShowPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleCustomerShowRouteQuery, queryRef);
  return <TradeRuleCustomerShow {...routeProps} {...queryData} />;
}

TradeRuleCustomerShowPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleCustomerShowRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleCustomerShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with `key={routeProps.match.params.uuid}`
 * so a uuid change disposes the stale `useQueryLoader` query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule customer show route content.
 */
function TradeRuleCustomerShowRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleCustomerShowRouteQuery,
    routeProps,
    { logLabel: 'TradeRuleCustomerShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRuleCustomerShowPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleCustomerShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleCustomerShow {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleCustomerShowRouteRenderer.propTypes = {
  routeProps: tradeRuleCustomerShowRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleCustomerShow route state supplied by found-relay.
 * Any network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it before pending props resolve. The
 * renderer is keyed on the route uuid so navigation between entities remounts
 * it and disposes the stale `useQueryLoader` query.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleCustomerShow route content or loading state.
 */
export function tradeRuleCustomerShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleCustomerShowRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleCustomerShowRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default TradeRuleCustomerShowRouteRenderer;
